import { format } from 'date-fns';
import { escapeHtml } from '../utils/escapeHtml';

interface EventData {
  name?: string;
  insuredName?: string;
  claimNumber?: string;
  lossCity?: string;
  lossState?: string;
  lossCounty?: string;
  lossZip?: string;
  assignmentType?: string;
  methodOfInspection?: string;
  targetQueue?: string;
  geoStatus?: string;
  mustStartAfter?: string;
  mustEndBefore?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  isTask?: boolean;
  isForced?: boolean;
  /** 'event' for blocked time; any other value is a task type */
  type?: string;
  /** Task reference ID - only present for tasks, not blocked time events */
  taskReferenceId?: string;
}

interface TooltipData {
  startClockHtml: string;
  endClockHtml: string;
  eventRecord: {
    id: string;
    data?: {
      name?: string;
    };
    originalData: EventData;
  };
}

/**
 * Get status label from target queue
 */
function getStatusLabel(targetQueue?: string): string {
  switch (targetQueue) {
    case 'dispatched_q':
      return 'Dispatched';
    case 'egress':
      return 'Complete';
    case 'needs_attention_q':
      return 'Attention Needed';
    case 'wip_q':
      return 'Started';
    case 'cancelled_q':
      return 'Cancelled';
    case 'manual_dispatch_q':
      return 'Needs Dispatching';
    case 'input_q':
    case 'auto_dispatch_q':
    default:
      return 'Processing';
  }
}

/**
 * Format appointment window from start/end times
 */
function formatAppointmentWindow(
  mustStartAfter?: string,
  mustEndBefore?: string
): string {
  if (!mustStartAfter || !mustEndBefore) return '-';
  try {
    const start = format(new Date(mustStartAfter), 'h:mm a');
    const end = format(new Date(mustEndBefore), 'h:mm a');
    return `${start} - ${end}`;
  } catch {
    return '-';
  }
}

/**
 * Format a date for appointment display (e.g. "Mar 24, 2026 8:00 AM")
 */
function formatAppointmentDate(date?: string | Date): string {
  if (!date) return '-';
  try {
    return format(new Date(date), 'MMM d, yyyy h:mm a');
  } catch {
    return '-';
  }
}

// Shared inline styles for tooltip fields (hover handled via CSS :hover in eventTooltipStyles)
const fieldStyle =
  'cursor: pointer; padding: 2px 4px; border-radius: 3px; transition: background-color 0.2s;';

/**
 * Build a tooltip field row with copy data attributes.
 * Hover effect is handled by CSS `.tooltip-field:hover` rule.
 */
function field(
  fieldName: string,
  value: string,
  label: string,
  displayValue?: string
): string {
  const shown = displayValue ?? value;
  return `<div class="tooltip-field" data-field="${fieldName}" data-value="${value}" style="${fieldStyle}"><strong>${label}:</strong> ${shown}</div>`;
}

/**
 * Generate HTML for event tooltip — matches v1 layout with inline styles.
 * Only shows tooltip for tasks, not for blocked time events.
 */
export function getEventTooltipTemplate(tooltipData: TooltipData): string {
  const event = tooltipData.eventRecord.originalData;

  // Only show tooltip for tasks (have taskReferenceId), not blocked time events
  const taskReferenceId = event?.taskReferenceId;
  const isTask = taskReferenceId && taskReferenceId !== '';
  if (!isTask) {
    return '';
  }

  // Same rule as the bar's forced marker. Blocked time linked to a task can
  // carry its own isForced (saved past an overlap check), which is not a
  // forced assignment.
  const isForcedTask =
    event?.isForced === true && event?.type?.toLowerCase() !== 'event';

  // Extract and escape all user-provided values to prevent XSS
  const rawName = tooltipData.eventRecord.data?.name || event?.name || '';
  const name = escapeHtml(rawName);
  const geoStatus = event?.geoStatus ? escapeHtml(event.geoStatus) : '';
  const appointmentWindow = escapeHtml(
    formatAppointmentWindow(event?.mustStartAfter, event?.mustEndBefore)
  );
  const statusLabel = escapeHtml(getStatusLabel(event?.targetQueue));
  const rawLocation = `${event?.lossCity || ''}, ${event?.lossState || ''} ${
    event?.lossZip || ''
  }`.trim();
  const location = escapeHtml(rawLocation);
  const insuredName = escapeHtml(event?.insuredName || '-');
  const lossCounty = escapeHtml(event?.lossCounty || '-');
  const assignmentType = escapeHtml(event?.assignmentType || '-');
  const methodOfInspection = event?.methodOfInspection
    ? escapeHtml(event.methodOfInspection)
    : '';

  // Arrived/Departed: show geoStatus (capitalized) for tasks, "-" otherwise
  const arrivedDeparted =
    event?.isTask && geoStatus
      ? geoStatus.charAt(0).toUpperCase() + geoStatus.slice(1)
      : '-';

  // Build copy data for the copy-all button (uses human-readable labels).
  // V1 parity: include every field rendered in the tooltip body — Forced and
  // Arrived/Departed were previously displayed but missing from the blob.
  const copyData = escapeHtml(
    JSON.stringify({
      'Claim Number': rawName,
      'Insured Name': event?.insuredName || '-',
      'Loss Location': rawLocation || '-',
      'Loss County': event?.lossCounty || '-',
      'Assignment Type': event?.assignmentType || '-',
      'Arrived/Departed': arrivedDeparted,
      Status: statusLabel || '-',
      Window: formatAppointmentWindow(
        event?.mustStartAfter,
        event?.mustEndBefore
      ),
      MOI: event?.methodOfInspection || '-',
      Forced: isForcedTask ? 'Yes' : '-',
      'Appointment Start': formatAppointmentDate(event?.startDate),
      'Appointment End': formatAppointmentDate(event?.endDate),
    })
  );

  // Status/MOI/Forced chips (hover handled via CSS :hover rules in eventTooltipStyles)
  const chipStyle =
    'cursor: pointer; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; transition: background-color 0.2s;';

  const statusChip = statusLabel
    ? `<span class="tooltip-field tooltip-chip-status" data-field="status" data-value="${statusLabel}" style="background: #e0e0e0; color: #333; ${chipStyle}">${statusLabel}</span>`
    : '';
  const moiChip = methodOfInspection
    ? `<span class="tooltip-field tooltip-chip-moi" data-field="moi" data-value="${methodOfInspection}" style="background: #e0e0e0; color: #333; ${chipStyle}">${methodOfInspection}</span>`
    : '';
  const forcedChip = isForcedTask
    ? `<span class="tooltip-field tooltip-chip-forced" data-field="forced" data-value="Forced Task" style="background: #f44336; color: white; ${chipStyle}">Forced Task</span>`
    : '';

  return `
    <div class="scheduler-event-tooltip" data-copy-all='${copyData}'>
      <div style="display: flex; align-items: center; margin-bottom: 4px;">
        <div style="flex: 1; display: flex; justify-content: center; position: relative; height: 16px;">
          <div class="tooltip-field-feedback" style="position: absolute; font-size: 12px; color: #28a745; opacity: 0; transition: opacity 0.2s; white-space: nowrap; pointer-events: none;">Copied!</div>
          <div class="tooltip-copy-feedback" style="position: absolute; font-size: 12px; color: #28a745; opacity: 0; transition: opacity 0.2s; white-space: nowrap; pointer-events: none;">Copied All!</div>
        </div>
        <div class="tooltip-copy-button" title="Copy All" style="cursor: pointer; padding: 4px; border-radius: 4px; display: flex; align-items: center; justify-content: center;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
          </svg>
        </div>
      </div>
      ${field('claimNumber', name, 'CL#')}
      ${field('insured', insuredName, 'Insured Name')}
      ${field('location', location || '-', 'Loss Location')}
      ${field('county', lossCounty, 'Loss County')}
      ${field('type', assignmentType, 'Assignment Type')}
      ${field('arrivedDeparted', arrivedDeparted, 'Arrived/Departed')}
      ${field('window', appointmentWindow, 'Appt. Window')}
      <div style="display: flex; gap: 8px; flex-wrap: wrap; margin: 4px 0; padding: 2px 4px;">
        ${statusChip}${moiChip}${forcedChip}
      </div>
      <div class="tooltip-field" data-field="startTime" data-value="${escapeHtml(
        formatAppointmentDate(event?.startDate)
      )}" style="margin: 8px 0 0 0; ${fieldStyle}">
        ${tooltipData.startClockHtml}
      </div>
      <div class="tooltip-field" data-field="endTime" data-value="${escapeHtml(
        formatAppointmentDate(event?.endDate)
      )}" style="margin: 8px 0 0 0; ${fieldStyle}">
        ${tooltipData.endClockHtml}
      </div>
    </div>
  `;
}

/**
 * CSS styles for scheduler sizing (tooltip styles are inline in the template)
 */
export const eventTooltipStyles = `
  /* Scheduler sizing - fill container, internal scroll only */
  #schedule .b-schedulerpro {
    flex: 1;
    width: 100%;
    height: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  /* Tooltip hover effects (CSP-compatible — replaces inline onmouseover/onmouseout) */
  .tooltip-field:hover { background-color: rgba(0,123,255,0.3); }
  .tooltip-copy-button:hover { background-color: rgba(0,123,255,0.3); }
  .tooltip-chip-status:hover,
  .tooltip-chip-moi:hover { background-color: rgba(0,123,255,0.3) !important; color: white !important; }
  .tooltip-chip-forced:hover { background-color: rgba(0,123,255,0.3) !important; }
`;
